import { useRef } from "react";
import type { CalendarViewMode } from "../types";
import { getWeekDays } from "./calendar-grid-helpers";

interface CalendarToolbarProps {
  viewMode: CalendarViewMode;
  onViewModeChange: (mode: CalendarViewMode) => void;
  weekStart: Date;
  onPrev: () => void;
  onNext: () => void;
  weekLabel: string;
  selectedDay?: Date;
  onSelectDay?: (day: Date) => void;
}

const DAY_TAB_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri"] as const;

function isSameDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function CalendarToolbar({
  viewMode,
  onViewModeChange,
  weekStart,
  onPrev,
  onNext,
  weekLabel,
  selectedDay,
  onSelectDay,
}: CalendarToolbarProps) {
  const weekDays = getWeekDays(weekStart);
  // Roving-tabindex ref map for the day-tab row. Keyed by tab index (0..4).
  const dayTabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleDayKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    idx: number,
  ) => {
    if (!onSelectDay) return;
    const count = weekDays.length;
    let nextIdx: number | null = null;

    if (e.key === "ArrowRight") nextIdx = (idx + 1) % count;
    else if (e.key === "ArrowLeft") nextIdx = (idx - 1 + count) % count;
    else if (e.key === "Home") nextIdx = 0;
    else if (e.key === "End") nextIdx = count - 1;

    if (nextIdx === null) return;
    e.preventDefault();
    const nextDay = weekDays[nextIdx as 0 | 1 | 2 | 3 | 4];
    onSelectDay(nextDay);
    // Defer focus to next tick so the re-render mounts the new active tab.
    requestAnimationFrame(() => {
      dayTabRefs.current[nextIdx as number]?.focus();
    });
  };

  return (
    <div data-testid="calendar-toolbar" className="mb-4">
      <div className="flex items-center gap-3 flex-wrap">
        {/* Prev / Next chevrons */}
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={onPrev}
            data-testid="calendar-prev"
            aria-label="Previous week"
            className="flex items-center justify-center w-[30px] h-[30px] rounded-md bg-surface border border-border-subtle text-text-muted hover:text-white hover:bg-surface-2 cursor-pointer"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onNext}
            data-testid="calendar-next"
            aria-label="Next week"
            className="flex items-center justify-center w-[30px] h-[30px] rounded-md bg-surface border border-border-subtle text-text-muted hover:text-white hover:bg-surface-2 cursor-pointer"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>

        <span
          data-testid="calendar-week-label"
          className="text-sm font-semibold text-white"
        >
          {weekLabel}
        </span>

        <div className="ml-auto">
          <div
            className="inline-flex bg-surface border border-border-subtle rounded-lg p-[3px]"
            role="group"
            aria-label="View mode"
          >
            <button
              type="button"
              onClick={() => onViewModeChange("week")}
              data-testid="view-mode-week"
              aria-pressed={viewMode === "week"}
              className={`px-3 py-1 rounded-md text-[13px] font-medium cursor-pointer ${
                viewMode === "week"
                  ? "bg-surface-2 text-white"
                  : "text-text-muted hover:text-white"
              }`}
            >
              Week
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange("day")}
              data-testid="view-mode-day"
              aria-pressed={viewMode === "day"}
              className={`px-3 py-1 rounded-md text-[13px] font-medium cursor-pointer ${
                viewMode === "day"
                  ? "bg-surface-2 text-white"
                  : "text-text-muted hover:text-white"
              }`}
            >
              Day
            </button>
          </div>
        </div>
      </div>

      {viewMode === "day" && selectedDay && onSelectDay && (
        <div
          data-testid="calendar-day-tabs"
          className="mt-3 inline-flex bg-surface border border-border-subtle rounded-lg p-[3px] gap-0.5"
          role="tablist"
          aria-label="Select day"
        >
          {weekDays.map((day, idx) => {
            const isActive = isSameDate(day, selectedDay);
            return (
              <button
                key={day.toISOString()}
                ref={(el) => {
                  dayTabRefs.current[idx] = el;
                }}
                type="button"
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                data-testid={`calendar-day-tab-${idx}`}
                onClick={() => onSelectDay(day)}
                onKeyDown={(e) => handleDayKeyDown(e, idx)}
                className={`px-3 py-1 rounded-md text-[13px] font-medium cursor-pointer ${
                  isActive
                    ? "bg-brand text-white"
                    : "text-text-muted hover:text-white"
                }`}
              >
                {DAY_TAB_NAMES[idx as 0 | 1 | 2 | 3 | 4]} {day.getDate()}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
